/**
 * Kích thước 2 chiều (khổ giấy, khổ in, khổ máy…) — chuẩn 04/11/2026: nhập bằng 2 ô số, lưu "R x D mm" (VD "650x860 mm").
 * Đọc được cả chuỗi cũ gõ tay: "65x86", "65 x 86 cm", "650*860", "1030x800 mm". Quy ước cũ: không ghi đơn vị và
 * cả hai số ≤ 160 → cm (giấy in offset), còn lại mm (cùng quy tắc parseSheetDimensionsMm của bộ lập kế hoạch).
 */
export interface SizeMm {
  width: number;
  length: number;
}

const num = (s: string) => parseFloat(s.replace(',', '.'));

/** Đọc chuỗi kích thước → mm; null nếu không đủ 2 số dương */
export function parseSizeMm(raw?: string | null): SizeMm | null {
  if (!raw || typeof raw !== 'string') return null;
  const cleaned = raw.trim().toLowerCase();
  const m = cleaned.match(/(\d+(?:[.,]\d+)?)\s*(?:mm|cm)?\s*[x*×]\s*(\d+(?:[.,]\d+)?)/i);
  if (!m) return null;
  let a = num(m[1]);
  let b = num(m[2]);
  if (!(a > 0) || !(b > 0)) return null;
  const hasMm = /mm/.test(cleaned);
  const hasCm = /cm/.test(cleaned) && !hasMm;
  if (hasCm || (!hasMm && a <= 160 && b <= 160)) {
    a *= 10;
    b *= 10;
  }
  const round = (v: number) => Math.round(v * 10) / 10;
  return { width: round(a), length: round(b) };
}

/** Chuỗi chuẩn lưu DB: "650x860 mm"; rỗng nếu thiếu số */
export function formatSizeMm(width?: number | null, length?: number | null): string {
  if (!(Number(width) > 0) || !(Number(length) > 0)) return '';
  const f = (v: number) => String(Math.round(Number(v) * 10) / 10);
  return `${f(width as number)}x${f(length as number)} mm`;
}

/** Chuẩn hoá một chuỗi kích thước bất kỳ về dạng lưu chuẩn (giữ nguyên nếu không đọc được) */
export function normalizeSizeText(raw?: string | null): string {
  const s = parseSizeMm(raw);
  return s ? formatSizeMm(s.width, s.length) : String(raw ?? '');
}

/** Kích thước 3 chiều (thùng carton…): Dài × Rộng × Cao mm — lưu "D x R x C mm" (VD "600x400x300 mm"), 05/11/2026 */
export interface BoxSizeMm {
  length: number;
  width: number;
  height: number;
}

/** Đọc "600x400x300", "60 x 40 x 30 cm", "600*400*300 mm" → mm; null nếu không đủ 3 số dương */
export function parseBoxSizeMm(raw?: string | null): BoxSizeMm | null {
  if (!raw || typeof raw !== 'string') return null;
  const cleaned = raw.trim().toLowerCase();
  const m = cleaned.match(/(\d+(?:[.,]\d+)?)\s*[x*×]\s*(\d+(?:[.,]\d+)?)\s*[x*×]\s*(\d+(?:[.,]\d+)?)/);
  if (!m) return null;
  const f = /cm/.test(cleaned) && !/mm/.test(cleaned) ? 10 : 1;
  const [l, w, h] = [m[1], m[2], m[3]].map((v) => Math.round(num(v) * f * 10) / 10);
  return l > 0 && w > 0 && h > 0 ? { length: l, width: w, height: h } : null;
}

/** "600x400x300 mm"; rỗng nếu thiếu số */
export function formatBoxSizeMm(length?: number | null, width?: number | null, height?: number | null): string {
  if (!(Number(length) > 0) || !(Number(width) > 0) || !(Number(height) > 0)) return '';
  const f = (v: number) => String(Math.round(Number(v) * 10) / 10);
  return `${f(length as number)}x${f(width as number)}x${f(height as number)} mm`;
}
